import { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { Tree, type NodeApi } from "react-arborist";
import { Flex, Text, TextField, IconButton } from "@radix-ui/themes";
import { MagnifyingGlassIcon, Cross1Icon } from "@radix-ui/react-icons";
import { createDragDropManager } from "dnd-core";
import { HTML5Backend } from "react-dnd-html5-backend";
import { CollectionTreeNode } from "./CollectionTreeNode";
import { CreateSnippetDialog } from "./CreateSnippetDialog";
import { CreateCollectionDialog } from "./CreateCollectionDialog";
import type { SnippetTreeNode } from "@/v2/entities/KnowledgeSnippet";
import { useCreateSnippet } from "@/v2/entities/KnowledgeSnippet";
import { useDebounce } from "@/v2/shared/hooks/useDebounce";
import styles from "./CollectionsSidebar.module.scss";

//Hack to make drag and drop work in tiptap when react-arborist is also present in the page
//Hopefully they will fix it soon.
let sharedDndManager: ReturnType<typeof createDragDropManager> | undefined;

const getSharedDndManager = () => {
  if (typeof window === "undefined") {
    return undefined;
  }
  if (!sharedDndManager) {
    sharedDndManager = createDragDropManager(HTML5Backend);
  }
  return sharedDndManager;
};

interface CollectionsSidebarProps {
  data: SnippetTreeNode[];
  selectedNodeId: string | null;
  onSnippetSelect: (snippetId: string) => void;
  onClearSelection: () => void;
  collectionPaths: string[];
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
}

export function CollectionsSidebar({
  data,
  selectedNodeId,
  onSnippetSelect,
  onClearSelection,
  collectionPaths,
  searchTerm: externalSearchTerm = "",
  onSearchChange,
}: CollectionsSidebarProps) {
  const [createSnippetDialogOpen, setCreateSnippetDialogOpen] = useState(false);
  const [createCollectionDialogOpen, setCreateCollectionDialogOpen] =
    useState(false);
  const [activeCollectionPath, setActiveCollectionPath] = useState<string>("");
  const [treeHeight, setTreeHeight] = useState<number>(0);
  const searchTerm = externalSearchTerm || "";
  const treeWrapperRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const dndManager = useMemo(() => getSharedDndManager(), []);

  const { mutateAsync: createSnippet } = useCreateSnippet();

  const treeData = useMemo(() => data, [data]);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Check if search has results by recursively checking tree
  const hasSearchResults = useMemo(() => {
    if (!debouncedSearchTerm || !treeData || treeData.length === 0) return true;

    const checkNode = (node: SnippetTreeNode): boolean => {
      const lowerTerm = debouncedSearchTerm.toLowerCase();
      const nameMatch = node.name.toLowerCase().includes(lowerTerm);
      const pathMatch = node.path?.toLowerCase().includes(lowerTerm);

      if (nameMatch || pathMatch) return true;

      if (node.children && node.children.length > 0) {
        return node.children.some(checkNode);
      }

      return false;
    };

    return treeData.some(checkNode);
  }, [treeData, debouncedSearchTerm]);

  const handleSearchMatch = useCallback(
    (node: NodeApi<SnippetTreeNode>, term: string) => {
      const lowerTerm = term.toLowerCase();
      const nameMatch = node.data.name.toLowerCase().includes(lowerTerm);
      const pathMatch = node.data.path?.toLowerCase().includes(lowerTerm);

      // Match both collections (folders) and snippets (files) by name or path
      return nameMatch || Boolean(pathMatch);
    },
    []
  );

  const updateTreeHeight = useCallback(() => {
    if (!treeWrapperRef.current) {
      return;
    }

    setTreeHeight(treeWrapperRef.current.clientHeight);
  }, []);

  const treeWrapperObserver = useCallback(
    (node: HTMLDivElement | null) => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      treeWrapperRef.current = node;

      if (!node) {
        return;
      }

      updateTreeHeight();

      if (
        typeof window === "undefined" ||
        typeof ResizeObserver === "undefined"
      ) {
        return;
      }

      const observer = new ResizeObserver(() => {
        updateTreeHeight();
      });

      observer.observe(node);
      resizeObserverRef.current = observer;
    },
    [updateTreeHeight]
  );

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.addEventListener("resize", updateTreeHeight);

    return () => {
      window.removeEventListener("resize", updateTreeHeight);
    };
  }, [updateTreeHeight]);

  const computedTreeHeight = useMemo(
    () => (treeHeight > 0 ? treeHeight : 1),
    [treeHeight]
  );

  const handleSnippetSelect = useCallback(
    (snippetId: string) => {
      onSnippetSelect(snippetId);
    },
    [onSnippetSelect]
  );

  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only clear if clicking directly on the container, not its children
      if (e.target === e.currentTarget) {
        onClearSelection();
      }
    },
    [onClearSelection]
  );

  const handleOpenCreateSnippet = useCallback((collectionPath: string) => {
    setActiveCollectionPath(collectionPath);
    setCreateSnippetDialogOpen(true);
  }, []);

  const handleOpenCreateCollection = useCallback((collectionPath: string) => {
    setActiveCollectionPath(collectionPath);
    setCreateCollectionDialogOpen(true);
  }, []);

  const handleCreateSnippetSubmit = useCallback(
    async (
      collectionPath: string,
      snippetName: string,
      isInternal: boolean
    ) => {
      const normalizedPath = collectionPath.startsWith("/")
        ? collectionPath.slice(1)
        : collectionPath;
      await createSnippet({
        path: normalizedPath,
        title: snippetName,
        isInternal,
      });
    },
    [createSnippet]
  );

  const handleCreateCollectionSubmit = useCallback(
    async (
      parentCollectionPath: string,
      collectionName: string,
      snippetName: string
    ) => {
      const normalizedParent = parentCollectionPath.startsWith("/")
        ? parentCollectionPath.slice(1)
        : parentCollectionPath;
      const collectionPath = normalizedParent
        ? `${normalizedParent}/${collectionName}`
        : collectionName;
      await createSnippet({
        path: collectionPath,
        title: snippetName,
        isInternal: false, // Default to external for collection creation
      });
    },
    [createSnippet]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange?.(e.target.value);
    },
    [onSearchChange]
  );

  const handleClearSearch = useCallback(() => {
    onSearchChange?.("");
  }, [onSearchChange]);

  return (
    <Flex
      className={styles.treeViewContainer}
      direction="column"
      p="4"
      pt="1"
      onClick={handleContainerClick}
    >
      {onSearchChange && (
        <Flex mb="3" className={styles.searchContainer}>
          <TextField.Root
            placeholder="Search snippets"
            size="2"
            value={externalSearchTerm}
            onChange={handleSearchChange}
          >
            <TextField.Slot>
              <MagnifyingGlassIcon height="16" width="16" />
            </TextField.Slot>
            {externalSearchTerm && (
              <TextField.Slot>
                <IconButton
                  size="1"
                  variant="ghost"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <Cross1Icon height="14" width="14" />
                </IconButton>
              </TextField.Slot>
            )}
          </TextField.Root>
        </Flex>
      )}
      <div className={styles.treeViewWrapper} ref={treeWrapperObserver}>
        {!data || data.length === 0 ? (
          <Flex
            className={styles.emptyContainer}
            direction="column"
            align="center"
            justify="center"
            gap="3"
            p="5"
          >
            <Text size="4" weight="medium">
              No Snippets Yet
            </Text>
            <Text
              size="3"
              color="gray"
              style={{ textAlign: "center", maxWidth: "400px" }}
            >
              Snippets organize the information your AI learns from. Create one
              to begin structuring your knowledge system.
            </Text>
          </Flex>
        ) : debouncedSearchTerm && !hasSearchResults ? (
          <Flex
            className={styles.emptySearchContainer}
            direction="column"
            align="center"
            justify="center"
            gap="2"
            p="5"
          >
            <Text size="4" weight="medium">
              No Snippets Found
            </Text>
            <Text size="3" color="gray" style={{ textAlign: "center" }}>
              Try adjusting your search.
            </Text>
          </Flex>
        ) : (
          <Tree
            data={treeData}
            openByDefault={false}
            width="100%"
            height={computedTreeHeight}
            indent={32}
            rowHeight={50}
            overscanCount={5}
            paddingTop={0}
            paddingBottom={0}
            selection={selectedNodeId || undefined}
            searchTerm={debouncedSearchTerm}
            searchMatch={handleSearchMatch}
            {...(dndManager ? { dndManager } : {})}
          >
            {(props) => (
              <CollectionTreeNode
                {...props}
                onSnippetSelect={handleSnippetSelect}
                onOpenCreateSnippet={handleOpenCreateSnippet}
                onOpenCreateCollection={handleOpenCreateCollection}
              />
            )}
          </Tree>
        )}
      </div>

      <CreateSnippetDialog
        open={createSnippetDialogOpen}
        onOpenChange={setCreateSnippetDialogOpen}
        defaultCollectionPath={activeCollectionPath}
        collectionPaths={collectionPaths}
        onCreateSnippet={handleCreateSnippetSubmit}
      />

      <CreateCollectionDialog
        open={createCollectionDialogOpen}
        onOpenChange={setCreateCollectionDialogOpen}
        defaultParentPath={activeCollectionPath}
        collectionPaths={collectionPaths}
        onCreateCollection={handleCreateCollectionSubmit}
      />
    </Flex>
  );
}
